import React, { useState, useRef, useEffect, useLayoutEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faAngleLeft, faAngleRight, faArrowLeft, faChevronDown, faChevronRight, faChevronUp,
    faThumbtack, faMagnifyingGlass,
} from '@fortawesome/free-solid-svg-icons';
import {
    DndContext, PointerSensor, useSensor, useSensors, closestCenter,
} from '@dnd-kit/core';
import {
    SortableContext, useSortable, arrayMove, verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { clearGameReturnIfLeft, gameReturnFor } from '../../utils/gameReturn.js';
import { DEFAULT_SCOPE } from '../../routes/navScopes.jsx';
import { LANDING_PATH, SUPER_USER_LANDING } from '../../config/accessControl.js';
import { SidebarSwitcherSlot } from './SidebarSwitcherSlot.jsx';
import { useNavTree } from './NavTreeContext.js';
import { collapsedSectionItem, isNodeActive, isSubtreeActive, matchesActivePath } from './navActive.js';
import { useSidebarResize } from './useSidebarResize.js';
import { GeoPlayLogo } from '../header/GeoPlayLogo.jsx';
import { HelpPopupButton } from '../header/HelpPopupButton.jsx';
import { useCommandPalette } from '../command-palette/CommandPaletteProvider.jsx';
import { updateDashboardContext } from '../../redux-store/ReduxStore.js';
import { updatePreferences } from '../../services/UserProfileService.jsx';

import './DashboardSidebar.css';

// Collapsed-sections state is UI-only and stays in localStorage
// (it doesn't need to follow the user across devices).
const SECTIONS_KEY = 'sidebarCollapsedSections';

// Where the scope back row lands for a scope with no list page of its own — the user's landing.
const HUB_PATH = LANDING_PATH;

const NO_LINKS = [];

const readArray = (key) => {
    try { const raw = localStorage.getItem(key); return raw ? JSON.parse(raw) : []; }
    catch { return []; }
};

const getExpandKey = (item) => item.path || `__label_${item.label}`;
const getSortId = (item) => item.path || item.label;

const getFirstLeafPath = (node) => {
    if (node.subItems && node.subItems.length > 0) return getFirstLeafPath(node.subItems[0]);
    return node.path;
};

// Walk a subtree and return the node whose path matches. Used so that
// clicking a parent still honors the leaf's own openInNewTab flag.
const findLeafByPath = (node, path) => {
    if (!node) return null;
    if (node.path === path) return node;
    if (node.subItems) {
        for (const sub of node.subItems) {
            const found = findLeafByPath(sub, path);
            if (found) return found;
        }
    }
    return null;
};

// Respects the `openInNewTab` flag on a nav item — opens the path in a new
// browser tab instead of routing in-app. Used by Help → Docs so the docs
// surface feels like a standalone reference window.
const goToPath = (item, path, onNavigate) => {
    if (item?.openInNewTab && path) {
        window.open(path, '_blank', 'noopener,noreferrer');
        return;
    }
    onNavigate(path);
};

const flattenLeaves = (nodes, acc = []) => {
    for (const n of nodes || []) {
        if (n.path && (!n.subItems || n.subItems.length === 0)) acc.push(n);
        if (n.subItems?.length) flattenLeaves(n.subItems, acc);
    }
    return acc;
};

const applyOrder = (items, order) => {
    if (!order || !order.length) return items;
    const index = new Map(items.map(i => [getSortId(i), i]));
    const ordered = [];
    order.forEach(id => {
        if (index.has(id)) { ordered.push(index.get(id)); index.delete(id); }
    });
    index.forEach(item => ordered.push(item));
    return ordered;
};

const getAncestorPaths = (links, currentPath, ancestors = []) => {
    for (const link of links || []) {
        if (link.subItems && link.subItems.length > 0) {
            const selfMatch = matchesActivePath(link, currentPath);
            const descendantMatch = link.subItems.some(
                sub =>
                    matchesActivePath(sub, currentPath) ||
                    (sub.subItems && getAncestorPaths([sub], currentPath).length > 0)
            );
            if (selfMatch || descendantMatch) ancestors.push(getExpandKey(link));
            getAncestorPaths(link.subItems, currentPath, ancestors);
        }
    }
    return ancestors;
};

const HoverPortal = ({ children, targetRect, level = 0 }) => {
    if (!targetRect) return null;
    const isNearBottom = targetRect.top > window.innerHeight / 2;
    const portalStyle = {
        position: 'fixed',
        left: `${targetRect.right + 10}px`,
        zIndex: 1000 + level,
        ...(isNearBottom
            ? { bottom: `${window.innerHeight - targetRect.bottom}px` }
            : { top: `${targetRect.top}px` }),
    };
    return createPortal(<div style={portalStyle}>{children}</div>, document.body);
};

const PortalSubMenu = ({ items, onNavigate, level, portalTimeoutRef, onCloseAll }) => {
    const [hoveredItem, setHoveredItem] = useState(null);
    const [hoveredRect, setHoveredRect] = useState(null);

    return (
        <div className="nav-portal-menu">
            {items.map((subItem) => {
                const hasChildren = subItem.subItems && subItem.subItems.length > 0;
                return (
                    <div
                        key={subItem.path || subItem.label}
                        className="nav-portal-item"
                        onClick={(e) => {
                            e.stopPropagation();
                            if (hasChildren) {
                                const leafPath = getFirstLeafPath(subItem);
                                if (leafPath) {
                                    const leaf = findLeafByPath(subItem, leafPath) || subItem;
                                    goToPath(leaf, leafPath, onNavigate);
                                    onCloseAll();
                                }
                            } else if (subItem.path) {
                                goToPath(subItem, subItem.path, onNavigate);
                                onCloseAll();
                            }
                        }}
                        onMouseEnter={(e) => {
                            if (hasChildren) {
                                setHoveredItem(subItem);
                                setHoveredRect(e.currentTarget.getBoundingClientRect());
                            } else {
                                setHoveredItem(null); setHoveredRect(null);
                            }
                        }}
                    >
                        <div className="nav-portal-item-content">
                            <div className="nav-portal-item-icon">{subItem.icon}</div>
                            <span className="nav-portal-item-label">{subItem.label}</span>
                        </div>
                        {hasChildren && <FontAwesomeIcon icon={faChevronRight} className="nav-portal-item-arrow" />}
                    </div>
                );
            })}
            {hoveredItem && hoveredRect && (
                <HoverPortal targetRect={hoveredRect} level={level + 1}>
                    <div
                        className="nav-portal nav-portal-nested"
                        onMouseEnter={() => clearTimeout(portalTimeoutRef.current)}
                        onMouseLeave={() => { portalTimeoutRef.current = setTimeout(onCloseAll, 100); }}
                    >
                        <PortalSubMenu
                            items={hoveredItem.subItems}
                            onNavigate={onNavigate}
                            level={level + 1}
                            portalTimeoutRef={portalTimeoutRef}
                            onCloseAll={onCloseAll}
                        />
                    </div>
                </HoverPortal>
            )}
        </div>
    );
};

/**
 * SortableList — renders an ordered, draggable group of NavItems.
 * Used at every level where children can be reordered (inside a section,
 * and inside any parent NavItem with children).
 */
const SortableList = ({
    items, orderKey, onReorder, renderItem,
}) => {
    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
    const handleDragEnd = ({ active, over }) => {
        if (!over || active.id === over.id) return;
        const ids = items.map(getSortId);
        const oldIndex = ids.indexOf(active.id);
        const newIndex = ids.indexOf(over.id);
        if (oldIndex === -1 || newIndex === -1) return;
        onReorder(orderKey, arrayMove(ids, oldIndex, newIndex));
    };
    return (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={items.map(getSortId)} strategy={verticalListSortingStrategy}>
                {items.map(renderItem)}
            </SortableContext>
        </DndContext>
    );
};

const SortableNavItem = ({ item, ...props }) => {
    const id = getSortId(item);
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.4 : 1,
    };
    return (
        <div ref={setNodeRef} style={style} className="sortable-nav-wrapper">
            <NavItem
                item={item}
                {...props}
                dragAttributes={attributes}
                dragListeners={listeners}
            />
        </div>
    );
};

const NavItem = ({
    item, level = 0, isCollapsed, activePath, onNavigate,
    expandedItems, onToggleExpand,
    pinnedPaths, onTogglePin, canPin = false,
    suppressActiveState = false,
    parentOrderKey, orderMap, onReorder,
    dragAttributes, dragListeners,
    badge,
}) => {
    const isPinned = pinnedPaths && item.path ? pinnedPaths.has(item.path) : false;
    const [portalRect, setPortalRect] = useState(null);
    const itemRef = useRef(null);
    const portalTimeoutRef = useRef(null);

    const hasSubItems = item.subItems && item.subItems.length > 0;
    const expandKey = getExpandKey(item);
    const isExpanded = expandedItems.has(expandKey);
    const isLeaf = !hasSubItems;

    const isDirectlyActive = !suppressActiveState && isNodeActive(item, activePath);
    const isParentActive = !suppressActiveState && !isDirectlyActive && hasSubItems && isSubtreeActive(item.subItems, activePath);

    const showPortalOnHover = isCollapsed || item.iconOnly;

    const handleMouseEnter = () => {
        if (showPortalOnHover && itemRef.current) {
            clearTimeout(portalTimeoutRef.current);
            setPortalRect(itemRef.current.getBoundingClientRect());
        }
    };
    const handleMouseLeave = () => {
        if (showPortalOnHover) {
            portalTimeoutRef.current = setTimeout(() => setPortalRect(null), 100);
        }
    };

    const handleClick = () => {
        if (item.isPlaceholder || item.isLocked) return;
        if (hasSubItems && !isCollapsed) onToggleExpand(expandKey);
        // Own path wins over the first child — see NavSection.handleHeaderClick.
        if (item.path) {
            goToPath(item, item.path, onNavigate);
        } else if (hasSubItems) {
            const firstPath = getFirstLeafPath(item);
            if (firstPath) {
                // Honor the leaf's own openInNewTab flag when clicking a parent.
                const leaf = findLeafByPath(item, firstPath) || item;
                goToPath(leaf, firstPath, onNavigate);
            }
        }
    };

    const handleExpandToggle = (e) => {
        e.stopPropagation();
        if (hasSubItems && !isCollapsed) onToggleExpand(expandKey);
    };
    const handlePinClick = (e) => {
        e.stopPropagation();
        if (onTogglePin && item.path) onTogglePin(item.path);
    };

    useEffect(() => () => clearTimeout(portalTimeoutRef.current), []);

    const showPinButton = !isCollapsed && !item.iconOnly && canPin && isLeaf && item.path;
    const showChevron = !isCollapsed && !item.iconOnly && hasSubItems;

    const childOrderKey = `${parentOrderKey || ''}::${item.label}`;
    const orderedChildren = hasSubItems
        ? applyOrder(item.subItems, orderMap?.[childOrderKey])
        : [];

    return (
        <>
            <div
                ref={itemRef}
                className={`nav-item level-${level} ${hasSubItems ? 'has-children' : ''} ${isDirectlyActive ? 'active' : ''} ${isParentActive ? 'parent-active' : ''} ${isCollapsed || item.iconOnly ? 'collapsed' : ''} ${item.isPlaceholder ? 'nav-item--placeholder' : ''} ${item.isLocked ? 'nav-item--locked' : ''}`}
                onClick={handleClick}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                {...(dragAttributes || {})}
                {...(dragListeners || {})}
            >
                <div className="nav-item-content">
                    <div className="nav-item-icon">
                        {item.icon}
                        {/* Collapsed sidebar: show a simple red dot. The
                         * actual count appears once the sidebar is expanded,
                         * because large counts (12, 147, …) look awkward
                         * floating on a narrow icon rail. */}
                        {typeof badge === "number" && badge > 0 && (isCollapsed || item.iconOnly) && (
                            <span className="nav-item-badge-dot" aria-label={`${badge} unread`} />
                        )}
                    </div>
                    {!isCollapsed && !item.iconOnly && <span className="nav-item-label">{item.label}</span>}
                    {typeof badge === "number" && badge > 0 && !isCollapsed && !item.iconOnly && (
                        <span className="nav-item-badge" aria-label={`${badge} unread`}>
                            {badge > 99 ? "99+" : badge}
                        </span>
                    )}
                </div>
                {(showPinButton || showChevron) && (
                    <div className="nav-item-actions">
                        {showPinButton && (
                            <button
                                type="button"
                                className={`nav-item-action ${isPinned ? 'nav-item-action--pinned' : ''}`}
                                onClick={handlePinClick}
                                aria-label={isPinned ? 'Unpin' : 'Pin'}
                                title={isPinned ? 'Unpin' : 'Pin'}
                            >
                                <FontAwesomeIcon icon={faThumbtack} />
                            </button>
                        )}
                        {showChevron && (
                            <button
                                type="button"
                                className="nav-item-action"
                                onClick={handleExpandToggle}
                                aria-label={isExpanded ? 'Collapse' : 'Expand'}
                            >
                                <FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} />
                            </button>
                        )}
                    </div>
                )}
            </div>

            {!isCollapsed && !item.iconOnly && isExpanded && hasSubItems && (
                <div className="nav-subitems">
                    <SortableList
                        items={orderedChildren}
                        orderKey={childOrderKey}
                        onReorder={onReorder}
                        renderItem={(subItem) => {
                            const subIsLeaf = !subItem.subItems || subItem.subItems.length === 0;
                            const subCanPin = subIsLeaf && !!subItem.path && !subItem.isPlaceholder && !suppressActiveState;
                            return (
                                <SortableNavItem
                                    key={getSortId(subItem)}
                                    item={subItem}
                                    level={level + 1}
                                    isCollapsed={false}
                                    activePath={activePath}
                                    onNavigate={onNavigate}
                                    expandedItems={expandedItems}
                                    onToggleExpand={onToggleExpand}
                                    pinnedPaths={pinnedPaths}
                                    onTogglePin={onTogglePin}
                                    canPin={subCanPin}
                                    suppressActiveState={suppressActiveState}
                                    parentOrderKey={childOrderKey}
                                    orderMap={orderMap}
                                    onReorder={onReorder}
                                />
                            );
                        }}
                    />
                </div>
            )}

            {showPortalOnHover && portalRect && (
                <HoverPortal targetRect={portalRect} level={level}>
                    <div
                        className="nav-portal"
                        onMouseEnter={() => clearTimeout(portalTimeoutRef.current)}
                        onMouseLeave={() => { portalTimeoutRef.current = setTimeout(() => setPortalRect(null), 100); }}
                    >
                        {!hasSubItems ? (
                            <div className="nav-portal-menu">
                                <div
                                    className="nav-portal-item"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (item.path) goToPath(item, item.path, onNavigate);
                                        setPortalRect(null);
                                    }}
                                >
                                    <div className="nav-portal-item-content">
                                        {item.icon && <div className="nav-portal-item-icon">{item.icon}</div>}
                                        <span className="nav-portal-item-label">{item.label}</span>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <>
                                {/* Top-level parents only — nested flyouts stay children-only. */}
                                {level === 0 && (
                                    <div className="nav-portal-header">
                                        {item.icon && <div className="nav-portal-item-icon">{item.icon}</div>}
                                        <span className="nav-portal-item-label">{item.label}</span>
                                    </div>
                                )}
                                <PortalSubMenu
                                    items={item.subItems}
                                    onNavigate={onNavigate}
                                    level={level}
                                    portalTimeoutRef={portalTimeoutRef}
                                    onCloseAll={() => setPortalRect(null)}
                                />
                            </>
                        )}
                    </div>
                </HoverPortal>
            )}
        </>
    );
};

const NavSection = ({
    section, isCollapsed, activePath, onNavigate,
    expandedItems, onToggleExpand,
    isSectionCollapsed, onToggleSectionCollapse,
    pinnedPaths, onTogglePin,
    orderMap, onReorder,
    // Optional numeric badge — rendered next to the section label when > 0.
    // Used e.g. for the Notifications entry to show the unread count.
    badge,
}) => {
    const hasSubItems = section.subItems && section.subItems.length > 0;
    const isLeafSection = !!section.path && !hasSubItems;
    const collapsed = isSectionCollapsed(section.label);
    const suppressActive = !!section.noActiveState;

    const orderKey = section.label;
    const orderedItems = useMemo(
        () => hasSubItems ? applyOrder(section.subItems, orderMap?.[orderKey]) : [],
        [section.subItems, orderMap, orderKey, hasSubItems]
    );

    const isDirectlyActive = !suppressActive && isNodeActive(section, activePath);
    const isParentActive = !suppressActive && !isDirectlyActive && hasSubItems && isSubtreeActive(section.subItems, activePath);

    if (isCollapsed) {
        return (
            <NavItem
                item={collapsedSectionItem(section)}
                level={0}
                isCollapsed
                activePath={activePath}
                onNavigate={onNavigate}
                expandedItems={expandedItems}
                onToggleExpand={onToggleExpand}
                suppressActiveState={suppressActive}
                badge={badge}
            />
        );
    }

    const handleHeaderClick = () => {
        if (section.isLocked) return;
        if (isLeafSection) { goToPath(section, section.path, onNavigate); return; }
        // A section with its own path owns a landing page (Games, Portfolio) — go there rather than
        // diving into the first child, which would skip the landing entirely.
        if (section.path) {
            // Arriving from elsewhere, the click only opens the section's page — collapsing the list
            // you just navigated to reads as the click misfiring. Clicking it again once you're
            // already there toggles.
            const alreadyHere = activePath === section.path;
            goToPath(section, section.path, onNavigate);
            if (hasSubItems && alreadyHere) onToggleSectionCollapse(section.label);
            return;
        }
        if (!hasSubItems) return;
        const firstLeafPath = getFirstLeafPath(section);
        if (firstLeafPath) {
            // Honor the leaf's own openInNewTab flag (e.g. Help → Docs).
            const leaf = findLeafByPath(section, firstLeafPath) || section;
            goToPath(leaf, firstLeafPath, onNavigate);
        }
        // Header click toggles the section open/closed both ways — same as an
        // item with children (NavItem.handleClick), not expand-only.
        onToggleSectionCollapse(section.label);
    };

    const handleArrowClick = (e) => {
        e.stopPropagation();
        onToggleSectionCollapse(section.label);
    };

    const sectionKey = (section.label || '').toLowerCase().replace(/\s+/g, '-');

    return (
        <div className={`nav-section nav-section--${sectionKey} ${collapsed ? 'nav-section--collapsed' : ''} ${isLeafSection ? 'nav-section--leaf' : ''}`}>
            <div
                className={`nav-section-header ${isDirectlyActive ? 'active' : ''} ${isParentActive ? 'parent-active' : ''} ${section.isLocked ? 'nav-section--locked' : ''}`}
                onClick={handleHeaderClick}
                role="button"
                tabIndex={0}
            >
                <div className="nav-section-header-left">
                    {section.icon && <span className="nav-section-icon">{section.icon}</span>}
                    <span className="nav-section-label">{section.label}</span>
                    {typeof badge === "number" && badge > 0 && (
                        <span className="nav-section-badge" aria-label={`${badge} unread`}>
                            {badge > 99 ? "99+" : badge}
                        </span>
                    )}
                </div>
                {hasSubItems && (
                    <FontAwesomeIcon
                        icon={collapsed ? faChevronRight : faChevronDown}
                        className="nav-section-arrow"
                        onClick={handleArrowClick}
                    />
                )}
            </div>

            {hasSubItems && !collapsed && (
                <div className="nav-section-items">
                    <SortableList
                        items={orderedItems}
                        orderKey={orderKey}
                        onReorder={onReorder}
                        renderItem={(subItem) => {
                            const isLeaf = !subItem.subItems || subItem.subItems.length === 0;
                            const canPin = isLeaf && !!subItem.path && !subItem.isPlaceholder && !suppressActive;
                            return (
                                <SortableNavItem
                                    key={getSortId(subItem)}
                                    item={subItem}
                                    level={0}
                                    isCollapsed={false}
                                    activePath={activePath}
                                    onNavigate={onNavigate}
                                    expandedItems={expandedItems}
                                    onToggleExpand={onToggleExpand}
                                    pinnedPaths={pinnedPaths}
                                    onTogglePin={onTogglePin}
                                    canPin={canPin}
                                    suppressActiveState={suppressActive}
                                    parentOrderKey={orderKey}
                                    orderMap={orderMap}
                                    onReorder={onReorder}
                                />
                            );
                        }}
                    />
                </div>
            )}
        </div>
    );
};

export const DashboardSidebar = ({ navLinks, scope }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const activePath = location.pathname;
    const { open: openCommandPalette, registerNavbar } = useCommandPalette();

    // Enable the Ctrl/Cmd+K command-palette shortcut only while the navbar is
    // actually on screen (navLinks present — an empty array is still the shell).
    const navbarPresent = !!navLinks;
    useEffect(() => {
        if (!navbarPresent) return;
        return registerNavbar();
    }, [navbarPresent, registerNavbar]);

    const { expanded, width, toggle, onDragStart, isDragging } = useSidebarResize();

    const [userExpandedItems, setUserExpandedItems] = useState(() => new Set());
    const [userCollapsedItems, setUserCollapsedItems] = useState(() => new Set());

    const isSuperUser = useSelector(state => state.userDetails?.superUser) || false;
    // Set while an AI action runs in the Game Information form (onboarding or settings): greys out and
    // blocks the nav links so the user can't (silently) try to navigate away mid-lookup.
    const navBusy = useSelector(state => state.dashboardContext?.navBusy);

    // A Super User's home is Management (they have no hub), so it carries no back row; their other scopes go
    // back to it rather than to the member homepage.
    const superUserHome = isSuperUser && scope?.id === 'admin';
    const isScoped = !!scope && scope.id !== DEFAULT_SCOPE;
    // Only the back row is dropped on their home - the scope's links still render (they are bottom sections,
    // which only a focused scope shows; dropping isScoped itself left the Super User's sidebar empty).
    const showBackRow = isScoped && !superUserHome;

    // The view's links - the scope's sections with the game / onboarding / portfolio pages swapped in. The shell
    // builds them once (useScopedNavLinks) and shares them, so the page header's section switcher walks this tree.
    const scopedLinks = useNavTree() || NO_LINKS;

    // Computed from the INJECTED tree, not navLinks — the game pages are swapped in there, and ancestors
    // of the active path have to include them or a deep link lands with its group collapsed.
    const autoExpandedItems = useMemo(
        () => new Set(getAncestorPaths(scopedLinks, activePath)),
        [scopedLinks, activePath]
    );
    const expandedItems = useMemo(() => {
        const merged = new Set(autoExpandedItems);
        userExpandedItems.forEach(k => merged.add(k));
        userCollapsedItems.forEach(k => merged.delete(k));
        return merged;
    }, [autoExpandedItems, userExpandedItems, userCollapsedItems]);

    const handleToggleExpand = useCallback((key) => {
        if (expandedItems.has(key)) {
            setUserExpandedItems(prev => { const n = new Set(prev); n.delete(key); return n; });
            setUserCollapsedItems(prev => new Set(prev).add(key));
        } else {
            setUserCollapsedItems(prev => { const n = new Set(prev); n.delete(key); return n; });
            setUserExpandedItems(prev => new Set(prev).add(key));
        }
    }, [expandedItems]);

    const [collapsedSections, setCollapsedSections] = useState(() => new Set(readArray(SECTIONS_KEY)));
    const isSectionCollapsed = useCallback((label) => collapsedSections.has(label), [collapsedSections]);
    const handleToggleSectionCollapse = useCallback((label) => {
        setCollapsedSections(prev => {
            const next = new Set(prev);
            if (next.has(label)) next.delete(label); else next.add(label);
            try { localStorage.setItem(SECTIONS_KEY, JSON.stringify([...next])); } catch {}
            return next;
        });
    }, []);

    // Pinned paths + item order are user preferences — sourced from Redux
    // (hydrated from GET /v1/web/user at login) and persisted via
    // PATCH /v1/web/user/preferences on every change.
    const dispatch = useDispatch();
    const sidebarPinnedArr = useSelector(state => state.dashboardContext?.sidebarPinned ?? []);
    const itemOrder = useSelector(state => state.dashboardContext?.sidebarOrder ?? {});
    const pinnedPaths = useMemo(() => new Set(sidebarPinnedArr), [sidebarPinnedArr]);

    const handleTogglePin = useCallback((path) => {
        const next = new Set(pinnedPaths);
        if (next.has(path)) next.delete(path); else next.add(path);
        const nextArr = [...next];
        dispatch(updateDashboardContext({ sidebarPinned: nextArr }));
        updatePreferences({ sidebarPinned: nextArr }).catch(err =>
            console.warn('[DashboardSidebar] Failed to persist pinned paths:', err.message));
    }, [dispatch, pinnedPaths]);

    const handleReorder = useCallback((orderKey, orderedIds) => {
        const next = { ...itemOrder, [orderKey]: orderedIds };
        dispatch(updateDashboardContext({ sidebarOrder: next }));
        updatePreferences({ sidebarOrder: next }).catch(err =>
            console.warn('[DashboardSidebar] Failed to persist sidebar order:', err.message));
    }, [dispatch, itemOrder]);

    const pinnedSection = useMemo(() => {
        if (pinnedPaths.size === 0) return null;
        const leaves = flattenLeaves(navLinks || []);
        const byPath = new Map(leaves.map(l => [l.path, l]));
        const pinnedItems = [...pinnedPaths]
            .map(p => byPath.get(p))
            .filter(Boolean)
            // Preserve the active-match overrides — without `exact` /
            // `matchPathPrefixes` a pinned item whose path is a prefix of a
            // sibling's subtree (e.g. Game Registration vs Game Workspace's
            // per-game URLs) would wrongly light up. See matchesActivePath.
            .map(leaf => ({
                path: leaf.path,
                label: leaf.label,
                icon: leaf.icon,
                exact: leaf.exact,
                matchPathPrefixes: leaf.matchPathPrefixes,
                matchPathPattern: leaf.matchPathPattern,
            }));
        if (!pinnedItems.length) return null;
        return {
            section: true,
            label: 'Pinned',
            icon: <FontAwesomeIcon icon={faThumbtack} style={{ transform: 'rotate(-45deg)' }} />,
            subItems: pinnedItems,
        };
    }, [pinnedPaths, navLinks]);

    // Leaving a scope clears the redux fallback as well as navigating, so the hub doesn't snap back
    // into the scope on the next render (the URL wins, but redux is what answers when it can't).
    // A scope whose root is a list page in the hub (/game, /portfolio) goes back to that list — where
    // the game or portfolio was opened from — rather than all the way out to the homepage. A game opened
    // from a portfolio's dashboard goes back there instead, under that portfolio's name (gameReturn).
    const gameReturn = scope?.id === 'game' ? gameReturnFor(activePath) : null;
    const exitTo = gameReturn?.path || (isSuperUser ? SUPER_USER_LANDING
        : (scope?.rootIsLanding && scope?.root ? scope.root : HUB_PATH));
    const exitLabel = gameReturn?.label || (isSuperUser ? 'Management' : scope?.label);
    // The return target only lives while the user stays inside that game.
    useEffect(() => { clearGameReturnIfLeft(activePath); }, [activePath]);
    const exitScope = useCallback(() => {
        dispatch(updateDashboardContext({ navScope: DEFAULT_SCOPE }));
        navigate(exitTo);
    }, [dispatch, navigate, exitTo]);

    const mainLinks = useMemo(
        () => (pinnedSection ? [pinnedSection, ...scopedLinks] : scopedLinks),
        [pinnedSection, scopedLinks]
    );

    const handleNavigate = useCallback((path) => navigate(path), [navigate]);

    // Keep the active nav item in view. Deep sections (Players, Data Intelligence, AI Studio) sit
    // below the fold, and their routes are served by separate layouts that REMOUNT the sidebar with
    // its scroll reset to the top — so the highlighted item would otherwise be off-screen, making it
    // look like the click "didn't stick". Re-run when the route changes, when links finish loading
    // (async permission filtering), and when expansion changes so the target exists in the DOM.
    // Prefer the directly-active leaf; fall back to the active/parent-active section header when its
    // section is collapsed. `block: 'nearest'` is a no-op when the item is already visible, so items
    // already on-screen don't jump.
    const navLinksRef = useRef(null);
    useLayoutEffect(() => {
        const container = navLinksRef.current;
        if (!container) return;
        const target = container.querySelector('.nav-item.active')
            || container.querySelector('.nav-section-header.active')
            || container.querySelector('.nav-section-header.parent-active');
        target?.scrollIntoView({ block: 'nearest' });
    }, [activePath, mainLinks, expandedItems, expanded]);

    // We render the shell even when navLinks is empty — pages like /demo (the
    // awaiting-org-approval landing) want no top items but still need the
    // bottom avatar popup (Profile / App Settings / Help / Logout) reachable.
    if (!navLinks) return null;

    return (
        <aside
            className={`navigation-sidebar-container ${expanded ? 'expanded' : 'collapsed'} ${isDragging ? 'dragging' : ''}`}
            style={{ width: `${width}px` }}
        >
            <div className="navigation-sidebar-header">
                <GeoPlayLogo expanded={expanded} subtitle="LiveOps" />
                {expanded && (
                    <button
                        className="navigation-sidebar-search"
                        onClick={openCommandPalette}
                        aria-label="Search (Ctrl+K)"
                        title="Search (Ctrl+K)"
                    >
                        <FontAwesomeIcon icon={faMagnifyingGlass} />
                    </button>
                )}
            </div>

            {/* Org switcher — the element itself is owned by DashboardHeader and portalled in here,
                so both copies share one org catalogue and one change handler. */}
            <SidebarSwitcherSlot className="nav-org-switcher" />

            {/* Focused scope: a back row out to the hub. The nav list below is keyed on the scope so
                switching context flows the new menu set in rather than mutating the old one. */}
            {showBackRow && (
                <button
                    type="button"
                    className="nav-scope-back"
                    onClick={exitScope}
                    aria-label={gameReturn ? `Back to ${exitLabel}` : `Back — leave ${scope.label || 'this view'}`}
                >
                    <FontAwesomeIcon icon={faArrowLeft} />
                    {expanded && <span className="nav-scope-back-label">{exitLabel}</span>}
                </button>
            )}

            <div className={`navigation-sidebar-nav-links ${navBusy ? 'is-busy' : ''}`} key={scope?.id} ref={navLinksRef}>
                {mainLinks.map((link, index) => {
                    // Never after the last section — it would render as a rule under nothing.
                    const needsDividerAfter =
                        (link.label === 'Settings' || link.label === 'Pinned')
                        && index < mainLinks.length - 1;
                    const node = (
                        <NavSection
                            key={`section-${link.label}-${index}`}
                            section={link}
                            isCollapsed={!expanded}
                            activePath={activePath}
                            onNavigate={handleNavigate}
                            expandedItems={expandedItems}
                            onToggleExpand={handleToggleExpand}
                            isSectionCollapsed={isSectionCollapsed}
                            onToggleSectionCollapse={handleToggleSectionCollapse}
                            pinnedPaths={pinnedPaths}
                            onTogglePin={handleTogglePin}
                            orderMap={itemOrder}
                            onReorder={handleReorder}
                        />
                    );
                    return needsDividerAfter ? (
                        <React.Fragment key={`wrap-${index}`}>
                            {node}
                            <div className="nav-top-divider" aria-hidden="true" />
                        </React.Fragment>
                    ) : node;
                })}
            </div>
            <div className="sidebar-bottom-row">
                <HelpPopupButton expanded={expanded} />
            </div>

            <div className="sidebar-resize-handle" onMouseDown={onDragStart} />

            <button
                className="sidebar-edge-toggle"
                onClick={toggle}
                aria-label={expanded ? 'Collapse sidebar' : 'Expand sidebar'}
            >
                <FontAwesomeIcon icon={expanded ? faAngleLeft : faAngleRight} />
            </button>
        </aside>
    );
};
